import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { Button } from '../../catalyst/button';
import { type CurationItem, type CurationSimilarItem } from './useCurationSession';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { PriorReviewsBanner } from './PriorReviewsBanner';
import { SimilarPagesStrip } from './SimilarPagesStrip';

// Minimum dwell on each page after its preview settles before the action
// controls unlock — stops blind confirm/skip on a page you haven't seen yet.
const CONTROLS_DELAY_MS = 1000;

// Renders the page under review plus its action controls. The parent keys this
// on `item.pageId`, so the gate state below resets cleanly on every new item.
export function CurrentItem({
  item,
  saving,
  pickerOpen,
  members,
  selectedMemberIds,
  membersSettled,
  blastRadius,
  confirmRadius,
  onToggleMember,
  onWrong,
  onConfirm,
  onSkip,
}: {
  item: CurationItem;
  saving: boolean;
  pickerOpen: boolean;
  /** Similar flagged pages offered for a grouped verdict (DR-3318); empty = solo review. */
  members: CurationSimilarItem[];
  selectedMemberIds: Set<string>;
  /** False while this anchor's cohort is still in flight — see the controls gate below. */
  membersSettled: boolean;
  /** Pages a correction lands on: this page plus every selected member. */
  blastRadius: number;
  /** Pages a confirm lands on — fewer when this page is uncategorized (DR-3318). */
  confirmRadius: number;
  onToggleMember: (pageId: string) => void;
  onWrong: () => void;
  onConfirm: () => void;
  onSkip: () => void;
}) {
  // Controls stay locked until the preview has settled (loaded or failed) AND the dwell
  // has elapsed AND the group cohort is known, and never while a save is in flight.
  // Without the cohort gate a keyboard verdict fired inside the first second would apply
  // to the anchor alone, silently dropping members the curator never got to see.
  const [previewSettled, setPreviewSettled] = useState(false);
  const [delayPassed, setDelayPassed] = useState(false);
  const ready = previewSettled && delayPassed && membersSettled && !saving;

  // The cohort's identity, not just its size. A cohort can still pop in late for the SAME
  // anchor (a slow fetch, or a refetch recovering from an error), and the dwell must start
  // again when it does — otherwise a verdict fires against a group that appeared a moment
  // ago and was never looked at. Adjusting state during render, not in an effect, is the
  // supported way to reset state on a prop change.
  const memberSignature = useMemo(
    () => members.map((member) => member.pageId).sort().join(','),
    [members],
  );
  const [dwellKey, setDwellKey] = useState(memberSignature);
  if (dwellKey !== memberSignature) {
    setDwellKey(memberSignature);
    setDelayPassed(false);
  }

  // Stable so PagePreview's ref callback below keeps a stable identity.
  const markPreviewSettled = useCallback(() => setPreviewSettled(true), []);

  useEffect(() => {
    if (!previewSettled) return;
    const timer = setTimeout(() => setDelayPassed(true), CONTROLS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [previewSettled, memberSignature]);

  // Keyboard shortcuts only fire once the controls are unlocked.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'SELECT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (!ready || pickerOpen) return;
      if (e.key === 'ArrowRight') onConfirm();
      if (e.key === 'ArrowLeft') onWrong();
      if (e.key === 'ArrowDown') onSkip();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ready, pickerOpen, onConfirm, onWrong, onSkip]);

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      {/* Preview card */}
      <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden">
        <PagePreview pageId={item.pageId} onSettled={markPreviewSettled} />
      </div>

      {/* Signal card */}
      <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-4 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            {item.currentCategory
              ? `Is it '${item.currentCategory}'?`
              : 'This page is uncategorized — is that correct?'}
          </span>
          <a
            href={`/document-pages/${item.pageId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline shrink-0"
          >
            View page ↗
          </a>
        </div>
        <SignalLine item={item} />
      </div>

      {/* Prior-verdict notice — only rendered when the page was already reviewed */}
      {item.priorReviews && item.priorReviews.length > 0 && (
        <PriorReviewsBanner reviews={item.priorReviews} />
      )}

      {/* Grouping strip — only when the anchor actually has similar flagged pages */}
      {members.length > 0 && (
        <SimilarPagesStrip
          members={members}
          selectedIds={selectedMemberIds}
          anchorCategory={item.currentCategory}
          onToggle={onToggleMember}
          disabled={saving}
        />
      )}

      {/* Action row. Each verdict button names its OWN blast radius and, for a confirm,
          the category every one of those pages ends at — a grouped ruling relabels the
          members that differ, so it is never cast blind. */}
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Button outline disabled={!ready} onClick={onWrong}>
          ← Wrong: pick category{blastRadius > 1 ? ` (${blastRadius} pages)` : ''}
        </Button>
        <Button color="indigo" disabled={!ready} onClick={onConfirm}>
          → Correct{confirmEffect(confirmRadius, item.currentCategory)}
        </Button>
        <Button plain disabled={!ready} onClick={onSkip}>
          ↓ Skip
        </Button>
      </div>

      <p className="text-center text-xs text-gray-400 dark:text-zinc-500">
        Keyboard: ← wrong · → correct · ↓ skip
      </p>
    </div>
  );
}

/**
 * What a grouped confirm actually does, in the button label: how many pages it lands on
 * and the category they all end at. An uncategorized anchor has no category to name — its
 * group is other uncategorized pages — so it falls back to the bare count.
 */
function confirmEffect(radius: number, category: string | null): string {
  if (radius <= 1) return '';
  return category ? ` · ${radius} pages → '${category}'` : ` (${radius} pages)`;
}

function PagePreview({ pageId, onSettled }: { pageId: string; onSettled: () => void }) {
  const [imgError, setImgError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = pagePreviewSrc(pageId, { as: 'png' });

  // Drives both the loading spinner (hidden once loaded) and the controls gate.
  const settle = useCallback(() => {
    setLoaded(true);
    onSettled();
  }, [onSettled]);

  // A cached image can finish loading before React attaches onLoad (notably on a
  // Strict Mode remount), so onLoad never fires and the gate would stay locked.
  // The ref runs on mount: if the element is already complete, settle right away.
  const captureImg = useCallback(
    (img: HTMLImageElement | null) => {
      if (img?.complete) settle();
    },
    [settle],
  );

  if (imgError) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-400 dark:text-zinc-500">
        Preview unavailable
      </div>
    );
  }

  return (
    <div className="relative flex min-h-64 items-center justify-center">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="size-8 animate-spin rounded-full border-b-2 border-indigo-600 dark:border-indigo-400" />
        </div>
      )}
      {/* Mouse-wheel zoom / pan, mirroring FullScreenImagePreview. Gated on
          `loaded` so the gate spinner isn't interactable; double-click resets
          to 1×. */}
      <TransformWrapper
        minScale={1}
        maxScale={8}
        initialScale={1}
        wheel={{ step: 0.05, disabled: !loaded }}
        panning={{ disabled: !loaded }}
        pinch={{ disabled: !loaded }}
        doubleClick={{ mode: 'reset', disabled: !loaded }}
        limitToBounds
      >
        <TransformComponent wrapperStyle={{ width: '100%' }} contentStyle={{ width: '100%' }}>
          <img
            ref={captureImg}
            src={src}
            alt="Page preview"
            className={clsx(
              'max-h-[60vh] w-full object-contain transition-opacity duration-200',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
            onLoad={settle}
            onError={() => {
              setImgError(true);
              onSettled();
            }}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

function SignalLine({ item }: { item: CurationItem }) {
  const { source, signal, suggestedCategory } = item;

  if (source === 'outlier') {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Looks more like <span className="font-medium text-gray-700 dark:text-zinc-200">'{suggestedCategory}'</span>
        {signal.rivalScore != null && signal.ownScore != null && (
          <> ({Math.round(signal.rivalScore * 100)}% vs {Math.round(signal.ownScore * 100)}%)</>
        )}
      </p>
    );
  }

  if (source === 'low_certainty') {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Low certainty
        {signal.ownScore != null && <> ({signal.ownScore.toFixed(2)})</>}
      </p>
    );
  }

  if (source === 'user_mismatch') {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        User suggested <span className="font-medium text-gray-700 dark:text-zinc-200">'{suggestedCategory}'</span>
      </p>
    );
  }

  if (source === 'second_opinion') {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Needs a second opinion
        {suggestedCategory && (
          <>
            {' '}
            · classifier suggests{' '}
            <span className="font-medium text-gray-700 dark:text-zinc-200">'{suggestedCategory}'</span>
          </>
        )}
      </p>
    );
  }

  if (source === 'completeness') {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Incomplete extraction
        {signal.note && <> — {signal.note}</>}
      </p>
    );
  }

  // manual_flag
  return (
    <p className="text-sm text-gray-500 dark:text-zinc-400">
      Flagged by <span className="font-medium text-gray-700 dark:text-zinc-200">{signal.flaggedBy ?? 'unknown'}</span>
      {signal.note && <> — {signal.note}</>}
    </p>
  );
}
